import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOrderLogicRules,
  getOrderLogicRule,
  createOrderLogicRule,
  updateOrderLogicRule,
  deleteOrderLogicRule,
  simulateRules,
  generatePayload,
  generateRuleFromDescription,
  type CreateOrderLogicRuleRequest,
  type SimulateRequest,
} from '../api/orderLogicRules';

export function useOrderLogicRules(trigger?: string) {
  return useQuery({
    queryKey: ['orderLogicRules', trigger],
    queryFn: () => getOrderLogicRules(trigger),
  });
}

export function useOrderLogicRule(urn: string) {
  return useQuery({
    queryKey: ['orderLogicRules', urn],
    queryFn: () => getOrderLogicRule(urn),
    enabled: !!urn,
  });
}

export function useCreateOrderLogicRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateOrderLogicRuleRequest) => createOrderLogicRule(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orderLogicRules'] });
    },
  });
}

export function useUpdateOrderLogicRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, data }: { urn: string; data: Partial<CreateOrderLogicRuleRequest> }) =>
      updateOrderLogicRule(urn, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orderLogicRules'] });
    },
  });
}

export function useDeleteOrderLogicRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (urn: string) => deleteOrderLogicRule(urn),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orderLogicRules'] });
    },
  });
}

export function useSimulateRules() {
  return useMutation({
    mutationFn: (data: SimulateRequest) => simulateRules(data),
  });
}

export function useGeneratePayload() {
  return useMutation({
    mutationFn: (request: { ruleUrn?: string; rule?: Partial<CreateOrderLogicRuleRequest> }) =>
      generatePayload(request),
  });
}

export function useGenerateRule() {
  return useMutation({
    mutationFn: (description: string) => generateRuleFromDescription(description),
  });
}
